import React from "react";
import { injectIntl } from "react-intl";
import { Checkbox, Spin, Icon } from "antd";
import RightsStateTag from "./RightsStateTag";
import "./RightsManager.css";

const TYPE_LABEL_IDS = {
  image: "rights_manager_type_image",
  video: "rights_manager_type_video",
  audio: "rights_manager_type_audio",
  document: "rights_manager_type_document"
};

const STATE_COLORS = {
  unassigned: "#faad14",
  expired: "#ff4d4f",
  expiring: "#fa8c16",
  valid: "#52c41a"
};

/**
 * Etiqueta del ícono de un archivo.
 *
 * Se prefiere la `extension` que manda el backend, que la saca de originalURL:
 * hay assets cuyo NOMBRE no lleva extensión —"BCO DE CHILE - Teletón Campeones
 * IVR"— y ahí el ícono quedaba en "FILE" aunque el archivo fuera un WAV. El
 * nombre queda como respaldo para datos viejos que no traigan el campo.
 */
function fileLabel(file) {
  if (file && file.extension) return String(file.extension).toUpperCase();
  const parts = ((file && file.name) || "").split(".");
  return parts.length > 1 ? parts.pop().toUpperCase() : "FILE";
}

function compositionText(folder, intl) {
  const order = ["unassigned", "expired", "expiring", "valid"];
  return order
    .filter(state => (folder.counts || {})[state] > 0)
    .map(state => `${folder.counts[state]} ${intl.formatMessage({ id: `rights_manager_composition_${state}` })}`)
    .join(" · ");
}

/** Agrupa los archivos directos de un nodo por tipo, "Fuera del módulo" al final. */
function groupFiles(files, intl, rightsState) {
  const contentByType = {};
  const outOfModule = [];

  (files || []).forEach(file => {
    if (rightsState && file.rightsState !== rightsState) return;
    if (!file.isContent) {
      // Los archivos fuera del módulo sólo son contexto cuando no hay un
      // filtro. Con un estado activo contaminan el resultado visual aunque
      // lleguen desde una respuesta/cache anterior del árbol.
      if (rightsState) return;
      outOfModule.push(file);
      return;
    }
    if (!contentByType[file.type]) contentByType[file.type] = [];
    contentByType[file.type].push(file);
  });

  const groups = Object.keys(contentByType).map(type => ({
    label: intl.formatMessage({
      id: TYPE_LABEL_IDS[type] || "rights_manager_type_other"
    }),
    files: contentByType[type]
  }));

  if (outOfModule.length > 0) {
    groups.push({
      label: intl.formatMessage({ id: "rights_manager_type_out_of_module" }),
      files: outOfModule,
      outOfModule: true
    });
  }

  return groups;
}

class RightsManagerTree extends React.Component {
  componentDidMount() {
    this.props.fetchNode("root", null);
  }

  componentDidUpdate(prevProps) {
    // Cambió el filtro de estado: el árbol entero queda obsoleto. Se pide de
    // nuevo sólo la raíz — reabrir cada nodo ya expandido sería más fiel,
    // pero complica bastante para un filtro que en la práctica se usa para
    // arrancar una búsqueda nueva, no para refinar una ya abierta.
    if (prevProps.rightsState !== this.props.rightsState) {
      this.props.onCollapseAll();
      this.props.fetchNode("root", null);
    }
  }

  handleToggleExpand = folderId => {
    const { expandedKeys, onToggleExpand, fetchNode } = this.props;
    const willExpand = !expandedKeys.has(folderId);
    onToggleExpand(folderId);
    // Siempre se vuelve a pedir, incluso si el nodo ya estaba en caché: si el
    // usuario cambió el filtro de estado mientras esta carpeta estaba
    // colapsada, `nodes[folderId]` todavía tiene el desglose del filtro
    // anterior y mostrarlo tal cual sería un dato incorrecto, no solo viejo.
    if (willExpand) {
      fetchNode(folderId, folderId);
    }
  };

  isImplicitlySelected(ancestorFolderId) {
    return !!ancestorFolderId;
  }

  renderFolderRow(folder, depth, ancestorSelectedId) {
    const { intl, expandedKeys, selected, onToggleSelect } = this.props;
    const key = `folder:${folder._id}`;
    const isExpanded = expandedKeys.has(folder._id);
    const implied = this.isImplicitlySelected(ancestorSelectedId);
    const checked = implied || selected.has(key);
    const presentStates = Object.keys(folder.counts || {}).filter(
      s => folder.counts[s] > 0
    );

    return (
      <React.Fragment key={key}>
        <tr className={`rm-row rm-folder-row ${isExpanded ? "open" : ""} ${checked ? "selected" : ""}`}>
          <td style={{ width: 44 }}>
            <Checkbox
              checked={checked}
              disabled={implied}
              onChange={() => onToggleSelect("folder", folder)}
            />
          </td>
          <td className="rm-name-cell">
            {/* La flecha, el ícono y el nombre abren y cierran la carpeta: una
                flecha de 10px es un blanco chico, y el nombre es lo que uno
                apunta. La casilla queda fuera —marcar para asignar en lote es
                otra intención— igual que en las filas de archivo, donde el
                nombre abre el visor. */}
            <div
              className="rm-tree-row rm-openable"
              style={{ paddingLeft: depth * 22 }}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              onClick={() => this.handleToggleExpand(folder._id)}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  this.handleToggleExpand(folder._id);
                }
              }}
            >
              <span className="rm-caret">{isExpanded ? "▾" : "▸"}</span>
              {/* Punto 8 de Ricardo. Antes eran dos cuadrados (▤/▣) que no
                  leían como carpeta; el ícono de antd sí, y además distingue
                  abierta de cerrada con la misma forma. */}
              <span className="rm-file-icon folder" aria-hidden="true">
                <Icon type={isExpanded ? "folder-open" : "folder"} theme="filled" />
              </span>
              <span className="rm-node-name">{folder.name}</span>
              <span className="rm-node-meta">
                {intl.formatMessage(
                  { id: "rights_manager_pieces_count" },
                  { count: folder.totalPieces }
                )}
                {/* El backend ya devolvía `totalFolders` —lo cuenta la misma
                    pasada que las piezas— pero la fila nunca lo mostraba, así
                    que una carpeta que sólo contiene subcarpetas parecía tener
                    su contenido escondido. */}
                {folder.totalFolders > 0 &&
                  ` · ${intl.formatMessage(
                    { id: "rights_manager_subfolders_count" },
                    { count: folder.totalFolders }
                  )}`}
                {folder.outOfModuleCount > 0 &&
                  ` · ${intl.formatMessage(
                    { id: "rights_manager_out_count" },
                    { count: folder.outOfModuleCount }
                  )}`}
              </span>
            </div>
          </td>
          <td className="rm-composition">
            {folder.totalPieces > 0 && <>
              <div className="rm-roll">
                {presentStates.map(state => (
                  <i key={state} style={{ background: STATE_COLORS[state], width: `${(folder.counts[state] / folder.totalPieces) * 100}%` }} />
                ))}
              </div>
              <div className="rm-rolltx">{compositionText(folder, intl)}</div>
            </>}
          </td>
          <td>
            <RightsStateTag state={folder.state} />
          </td>
          <td>—</td><td>—</td><td>—</td><td>—</td>
        </tr>
        {isExpanded &&
          this.renderNode(
            folder._id,
            depth + 1,
            checked ? folder._id : ancestorSelectedId
          )}
      </React.Fragment>
    );
  }

  renderFileRow(file, depth, ancestorSelectedId, nodeKey) {
    const { selected, onToggleSelect, onOpenDetail, intl } = this.props;
    const key = `file:${file._id}`;
    const implied = this.isImplicitlySelected(ancestorSelectedId);
    const checked = implied || selected.has(key);
    const selectable = file.isContent;
    const abrir = () => onOpenDetail && onOpenDetail(file, nodeKey);

    return (
      <tr key={key} className={`rm-row ${!selectable ? "out" : ""} ${checked ? "selected" : ""}`}>
        <td style={{ width: 44 }}>
          <Checkbox
            checked={selectable && checked}
            disabled={!selectable || implied}
            onChange={() => onToggleSelect("file", file)}
          />
        </td>
        {/* El nombre abre el archivo en el visor. La casilla queda fuera a
            propósito: marcar para asignar en lote y abrir para mirar son dos
            intenciones distintas y comparten fila. */}
        <td className="rm-name-cell">
          <div
            className="rm-tree-row rm-openable"
            style={{ paddingLeft: depth * 22 }}
            role="button"
            tabIndex={0}
            title={intl.formatMessage({ id: "rights_manager_open_asset" })}
            onClick={abrir}
            onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                abrir();
              }
            }}
          >
            <span className="rm-file-icon">{fileLabel(file)}</span>
            <span className="rm-node-name">{file.name}</span>
          </div>
        </td>
        <td className="rm-composition" />
        <td>
          <RightsStateTag state={selectable ? file.rightsState : null} />
        </td>
        <td>{file.dueDate ? new Date(file.dueDate).toLocaleDateString() : "—"}</td>
        <td>{file.username || "—"}</td>
        <td>
          {file.createdAt ? new Date(file.createdAt).toLocaleDateString() : "—"}
        </td>
        <td>{file.rightsAssignedByName || "—"}</td>
      </tr>
    );
  }

  renderNode(nodeKey, depth, ancestorSelectedId) {
    const { nodes, intl } = this.props;
    const node = nodes[nodeKey];

    if (!node || node.loading) {
      return (
        <tr className="rm-table-loading-row">
          <td colSpan={8}>
            <div className="rm-table-loading">
              <Spin size="large" />
              <span>{intl.formatMessage({ id: "rights_manager_loading" })}</span>
            </div>
          </td>
        </tr>
      );
    }
    if (node.error) {
      return (
        <tr>
          <td colSpan={8} style={{ color: "#ff4d4f", paddingLeft: depth * 22 }}>
            {intl.formatMessage({ id: "rights_manager_load_error" })}
          </td>
        </tr>
      );
    }

    const groups = groupFiles(node.files, intl, this.props.rightsState);
    // Con cualquier filtro puesto, una carpeta sin piezas que lo cumplan no
    // tiene por qué aparecer: abrirla no muestra nada. Sin filtros sí se listan
    // las que sólo contienen archivos "No aplica" —el módulo no los cubre, pero
    // la carpeta existe y esconderla haría dudar de dónde quedó.
    //
    // El backend ya cuenta `totalPieces` con el filtro de tipo aplicado, así
    // que acá alcanza con no mostrar las que dan cero.
    const { rightsState, types } = this.props;
    const hayFiltro = Boolean(rightsState) || (types && types.length > 0);
    const visibleFolders = (node.folders || []).filter(folder => {
      if (hayFiltro) return Number(folder.totalPieces || 0) > 0;
      return Number(folder.totalPieces || 0) > 0 || Number(folder.outOfModuleCount || 0) > 0;
    });
    const hasVisibleContent = visibleFolders.length > 0 && groups.length > 0
      ? true
      : visibleFolders.length > 0 || groups.some(group => group.files.length > 0);

    return (
      <>
        {visibleFolders.map(folder =>
          this.renderFolderRow(folder, depth, ancestorSelectedId)
        )}
        {groups.map(group => (
          <React.Fragment key={group.label}>
            <tr>
              <td />
              <td colSpan={7}>
                <div
                  className="rm-type-header"
                  style={{ paddingLeft: depth * 22 }}
                >
                  <span>
                    {group.label} · {group.files.length}
                  </span>
                  <span className="ln" />
                </div>
              </td>
            </tr>
            {group.files.map(file =>
              this.renderFileRow(
                file,
                depth + 1,
                group.outOfModule ? null : ancestorSelectedId,
                nodeKey
              )
            )}
          </React.Fragment>
        ))}
        {!hasVisibleContent && (
          <tr className="rm-empty-row">
            <td colSpan={8}>
              {intl.formatMessage({ id: "rights_manager_empty" })}
            </td>
          </tr>
        )}
      </>
    );
  }

  render() {
    const { intl } = this.props;
    return (
      <div className="rm-tbl" style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 4, overflow: "auto" }}>
        <table className="rm-table rm-tree" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#fafafa" }}>
              <th style={{ width: 44 }} />
              <th style={{ textAlign: "left", padding: "12px 14px" }}>
                {intl.formatMessage({ id: "rights_manager_col_name" })}
              </th>
              <th style={{ textAlign: "left", padding: "12px 14px", width: 190 }}>
                {intl.formatMessage({ id: "rights_manager_col_composition" })}
              </th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>
                {intl.formatMessage({ id: "rights_manager_col_state" })}
              </th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>
                {intl.formatMessage({ id: "rights_manager_col_due_date" })}
              </th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>
                {intl.formatMessage({ id: "rights_manager_col_uploaded_by" })}
              </th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>
                {intl.formatMessage({ id: "rights_manager_col_uploaded_at" })}
              </th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>
                {intl.formatMessage({ id: "rights_manager_col_assigned_by" })}
              </th>
            </tr>
          </thead>
          <tbody>{this.renderNode("root", 0, null)}</tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(RightsManagerTree);
